import { CommandTooltip } from '@modules/common/components/RichTextEditor/CommandTooltip';
import { LinkButton } from '@modules/common/components/RichTextEditor/LinkButton';
import { TypographyFormattingButton } from '@modules/common/components/RichTextEditor/TypographyFormattingButton';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicOutlinedIcon from '@mui/icons-material/FormatItalicOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import FormatListNumberedOutlinedIcon from '@mui/icons-material/FormatListNumberedOutlined';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import StrikethroughSOutlinedIcon from '@mui/icons-material/StrikethroughSOutlined';
import {
  Box,
  ButtonProps,
  Divider,
  IconButton,
  Paper,
  Stack,
  Theme,
  alpha,
} from '@mui/material';
import { useCurrentEditor } from '@tiptap/react';

interface RichTextEditorToolbarProps {
  forceCloseBubbleMenu?: () => void;
}

export function RichTextEditorToolbar({
  forceCloseBubbleMenu,
}: RichTextEditorToolbarProps) {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  const getButtonProps = (
    command:
      | 'bold'
      | 'italic'
      | 'strike'
      | 'code'
      | 'link'
      | 'bulletList'
      | 'orderedList'
      | 'blockquote'
      | 'dividerLine'
      | null,
  ): ButtonProps => {
    return {
      size: 'small',
      sx: {
        borderRadius: 1.5,
        '&:hover': {
          color: 'primary.main',
          backgroundColor: (theme: Theme) =>
            alpha(theme.palette.primary.main, 0.1),
        },
        backgroundColor: (theme: Theme) =>
          command && editor.isActive(command)
            ? alpha(theme.palette.primary.main, 0.1)
            : undefined,
        color:
          command && editor.isActive(command)
            ? 'primary.main'
            : 'text.secondary',
      },
    };
  };

  const boldButton = (
    <CommandTooltip name="Bold text" shortcut={['$mod', 'B']}>
      <IconButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        {...getButtonProps('bold')}
      >
        <FormatBoldIcon fontSize="small" />
      </IconButton>
    </CommandTooltip>
  );

  const blockquoteButton = (
    <CommandTooltip name="Block quote" shortcut={['$mod', 'Alt', '5']}>
      <Box component="span">
        <IconButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          disabled={
            editor.isActive('bulletList') || editor.isActive('orderedList')
          }
          {...getButtonProps('blockquote')}
        >
          <FormatQuoteIcon fontSize="small" />
        </IconButton>
      </Box>
    </CommandTooltip>
  );

  const bulletListButtons = (
    <>
      <CommandTooltip name="Numbered list" shortcut={['$mod', 'Alt', '7']}>
        <IconButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          {...getButtonProps('orderedList')}
        >
          <FormatListNumberedOutlinedIcon fontSize="small" />
        </IconButton>
      </CommandTooltip>

      <CommandTooltip name="Bulleted list" shortcut={['$mod', 'Alt', '8']}>
        <IconButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          {...getButtonProps('bulletList')}
        >
          <FormatListBulletedOutlinedIcon fontSize="small" />
        </IconButton>
      </CommandTooltip>
    </>
  );

  const italicButton = (
    <CommandTooltip name="Italic text" shortcut={['$mod', 'I']}>
      <IconButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        {...getButtonProps('italic')}
      >
        <FormatItalicOutlinedIcon fontSize="small" />
      </IconButton>
    </CommandTooltip>
  );

  const strikeThroughButton = (
    <CommandTooltip name="Strikethrough" shortcut={['$mod', 'Shift', 'S']}>
      <IconButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        {...getButtonProps('strike')}
      >
        <StrikethroughSOutlinedIcon fontSize="small" />
      </IconButton>
    </CommandTooltip>
  );

  const codeButton = (
    <CommandTooltip name="Code" shortcut={['$mod', 'E']}>
      <IconButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        {...getButtonProps('code')}
      >
        <CodeOutlinedIcon fontSize="small" />
      </IconButton>
    </CommandTooltip>
  );

  const horizontalRuleButton = (
    <CommandTooltip name="Divider Line" shortcut={['$mod', 'Alt', '9']}>
      <IconButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        {...getButtonProps('dividerLine')}
      >
        <HorizontalRuleIcon fontSize="small" />
      </IconButton>
    </CommandTooltip>
  );

  return (
    <Stack
      component={Paper}
      elevation={2}
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        p: 0.5,
        border: () => '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <TypographyFormattingButton {...getButtonProps(null)} />

      {boldButton}
      {italicButton}
      {strikeThroughButton}
      {codeButton}
      {blockquoteButton}

      <Divider orientation="vertical" variant="middle" flexItem />

      {bulletListButtons}

      <Divider orientation="vertical" variant="middle" flexItem />

      {horizontalRuleButton}

      <LinkButton
        {...getButtonProps('link')}
        closeBubbleMenu={forceCloseBubbleMenu}
      />
    </Stack>
  );
}
